import { useReactiveVar } from '@apollo/client';
import moment from 'moment';
import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import {
	expectedUserVar,
	useFeature,
	useQueryParameters,
	useViewerRoles,
	useWorkspace,
	useWorkspaceAlias,
} from '@treams/treams-hooks';
import { FeatureName, type Group, GroupType } from '@treams/treams-network';
import { useToasts } from '@treams/treams-ui';

import {
	type GroupSelectionData,
	useGroupSelection,
} from '../../../../src/goals/group-selector/new/useGroupSelection';
import type { CycleAnalytics, CycleTemplatesListResult } from './cycles/models/CycleTypes';
import { GROUPS, SYNC, analyticsDatabaseName, clearDatabase } from './indexedDB';
import type { ScoreAnalytics, ScoreSeriesEntry } from './score/models/ScoreTypes';
import {
	type AnalyticsFilters,
	AnalyticsStatus,
	type WorkerRequest,
	type WorkerResponse,
} from './types';
import useFilters from './useFilters';
import AnalyticsWorker from './worker?worker';

// The live end-date analytics plus the optional history points requested
// alongside it; `series` is empty when no history was asked for.
export type ScoreResult = { analytics: ScoreAnalytics; series: ScoreSeriesEntry[] };

export interface AnalyticsContextValue {
	status: AnalyticsStatus;
	loading: boolean;
	error: Error | null;
	lastSyncedAt: string;
	filters: AnalyticsFilters;
	showEngagementAnalytics: boolean;
	groupSelection: GroupSelectionData;
	loadMetrics: (options?: { generate?: boolean }) => void;
	selectableGroupsFilter: (group: Group) => boolean;
	requestRecord: (store: string, key: string) => Promise<Record<string, unknown> | null>;
	requestCycles: (
		cycleTemplateID: string,
		startDate: Date,
		endDate: Date,
		groupIDs?: string[],
		roleID?: string,
	) => Promise<CycleAnalytics>;
	requestCycleTemplates: () => Promise<CycleTemplatesListResult>;
	requestScore: (endDate: Date, seriesEndDates?: Date[]) => Promise<ScoreResult>;
	requestQuery: (
		source: string,
		pipeline: Record<string, unknown>[],
	) => Promise<Record<string, unknown>[]>;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

interface IPendingRequest<T> {
	resolve: (data: T) => void;
	reject: (error: Error) => void;
}

function createHandshake() {
	let resolve!: () => void;

	const promise = new Promise<void>((r) => {
		resolve = r;
	});

	return { promise, resolve };
}

const ANALYTICS_LOADING_STATUSES: AnalyticsStatus[] = [
	AnalyticsStatus.Idle,
	AnalyticsStatus.Opening,
	AnalyticsStatus.Loading,
	AnalyticsStatus.Ingesting,
];

export function AnalyticsProvider({ children }: { children: ReactNode }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const history = useHistory();
	// The same assertion the worker sends as X-Treams-User, so the database is
	// keyed by exactly the identity its snapshot was fetched as.
	const userID = useReactiveVar(expectedUserVar);
	const filters = useFilters(userID);
	const roles = useViewerRoles();
	const workspace = useWorkspace();
	const workspaceAlias = useWorkspaceAlias();
	const [status, setStatus] = useState<AnalyticsStatus>(AnalyticsStatus.Idle);
	const { view } = useQueryParameters(['view']);
	const workerRef = useRef<Worker | null>(null);
	const [error, setError] = useState<Error | null>(null);
	const hasTeamAnalytics = useFeature(FeatureName.AnalyticsTeams);
	// Init handshake, recreated on every worker teardown.
	const readyRef = useRef<{ promise: Promise<void>; resolve: () => void }>(createHandshake());
	const [workerReset, setWorkerReset] = useState(0);
	// True when the worker found an empty database on init: forces a load even
	// when the localStorage sync marker is still fresh.
	const [dbEmpty, setDbEmpty] = useState(false);
	const showEngagementAnalytics = roles.isEngagementOfficer && view !== 'all';

	const recordPending = useRef<Map<string, IPendingRequest<Record<string, unknown> | null>>>(
		new Map(),
	);
	const cyclesPending = useRef<Map<string, IPendingRequest<CycleAnalytics>>>(new Map());
	const cycleTemplatesPending = useRef<Map<string, IPendingRequest<CycleTemplatesListResult>>>(
		new Map(),
	);
	const scorePending = useRef<Map<string, IPendingRequest<ScoreResult>>>(new Map());
	const queryPending = useRef<Map<string, IPendingRequest<Record<string, unknown>[]>>>(new Map());

	// Empty until the session slots seed the assertion; every consumer below
	// waits for it rather than opening a database under the wrong name.
	const database = userID ? analyticsDatabaseName(workspaceAlias, userID) : '';
	const sync = database ? `${SYNC}:${database}` : '';
	const selectableKey = database ? `${GROUPS}:${database}` : '';

	const [lastSyncedAt, setLastSyncedAt] = useState<string>('');

	const selectableGroupsFilter = useMemo(() => {
		return (group: Group) => {
			if (showEngagementAnalytics) return true;

			const canViewTeam = hasTeamAnalytics && group.type === GroupType.Team;
			const isDepartment = group.type === GroupType.Department;

			return group.viewerCanViewAnalytics && (canViewTeam || isDepartment);
		};
	}, [hasTeamAnalytics, showEngagementAnalytics]);

	const userCanAccessAllGroups = roles.isWorkspaceSuperuser || showEngagementAnalytics;

	const groupSelection = useGroupSelection({
		canSelect: selectableGroupsFilter,
		setPermissionBasedDefault: !userCanAccessAllGroups,
		// Omitted until the user is known: an unscoped key would leak the
		// previous account's selection into this one.
		localStorageKey: userID ? `analytics-groups:${userID}` : undefined,
	});

	// Key the worker lifecycle on the serialized ids, not the array identity:
	// useGroupSelection recreates the array with identical content, and a
	// teardown on identity churn aborts an in-flight ingest.
	const selectableIds = useMemo(
		() =>
			(groupSelection?.selectableGroups ?? [])
				.map((group) => group.id)
				.sort()
				.join(','),
		[groupSelection?.selectableGroups],
	);

	// Barrier raised while a load/ingest cycle is in flight: the worker clears
	// all stores before re-ingesting, so queries must not run in that window.
	const syncBarrierRef = useRef<{ promise: Promise<void>; resolve: () => void } | null>(null);

	const raiseSyncBarrier = useCallback(() => {
		if (syncBarrierRef.current) return;

		let resolve!: () => void;
		const promise = new Promise<void>((r) => {
			resolve = r;
		});

		syncBarrierRef.current = { promise, resolve };
	}, []);

	const lowerSyncBarrier = useCallback(() => {
		syncBarrierRef.current?.resolve();
		syncBarrierRef.current = null;
	}, []);

	// Re-check after every await: a worker reset swaps in a new handshake and
	// a load cycle can raise the barrier again.
	const awaitReady = useCallback(async () => {
		for (;;) {
			const ready = readyRef.current;

			await ready.promise;

			const barrier = syncBarrierRef.current;

			if (!barrier) {
				if (readyRef.current === ready) return;
				continue;
			}

			await barrier.promise;
		}
	}, []);

	const loadMetrics = useCallback(
		async (options?: { generate?: boolean }) => {
			if (!workspace.id) return;

			// A load is already in flight (barrier raised): posting another 'load'
			// would only abort the running one inside the worker.
			if (syncBarrierRef.current) return;

			// Raise before any await so queries issued in the same tick already block.
			raiseSyncBarrier();

			// Re-await when the handshake was swapped mid-wait.
			while (true) {
				const ready = readyRef.current;

				await ready.promise;

				if (readyRef.current === ready) break;
			}

			setError(null);

			workerRef.current?.postMessage({
				type: 'load',
				workspace: workspace.id,
				// The worker authenticates with the session cookie; forward this
				// tab's user assertion so a stale tab fails fast server-side.
				user: expectedUserVar(),
				generate: options?.generate,
			});
		},
		[workspace.id, raiseSyncBarrier],
	);

	const requestRecord = useCallback(
		async (store: string, key: string): Promise<Record<string, unknown> | null> => {
			await awaitReady();

			return new Promise((resolve, reject) => {
				const id = window.crypto.randomUUID();

				recordPending.current.set(id, { resolve, reject });

				const request: WorkerRequest = {
					type: 'record',
					id,
					store,
					key,
				};

				workerRef.current?.postMessage(request);
			});
		},
		[awaitReady],
	);

	const requestCycles = useCallback(
		async (
			cycleTemplateID: string,
			startDate: Date,
			endDate: Date,
			groupIDs?: string[],
			roleID?: string,
		): Promise<CycleAnalytics> => {
			await awaitReady();

			return new Promise((resolve, reject) => {
				const id = window.crypto.randomUUID();

				cyclesPending.current.set(id, { resolve, reject });

				const request: WorkerRequest = {
					type: 'cycles',
					id,
					cycleTemplateID,
					startDate,
					endDate,
					groupIDs,
					roleID,
				};

				workerRef.current?.postMessage(request);
			});
		},
		[awaitReady],
	);

	const requestCycleTemplates = useCallback(async (): Promise<CycleTemplatesListResult> => {
		await awaitReady();

		return new Promise((resolve, reject) => {
			const id = window.crypto.randomUUID();

			cycleTemplatesPending.current.set(id, { resolve, reject });

			const request: WorkerRequest = { type: 'cycleTemplates', id };

			workerRef.current?.postMessage(request);
		});
	}, [awaitReady]);

	const requestScore = useCallback(
		async (endDate: Date, seriesEndDates?: Date[]): Promise<ScoreResult> => {
			await awaitReady();

			return new Promise((resolve, reject) => {
				const id = window.crypto.randomUUID();

				scorePending.current.set(id, { resolve, reject });

				const request: WorkerRequest = { type: 'score', id, endDate, seriesEndDates };

				workerRef.current?.postMessage(request);
			});
		},
		[awaitReady],
	);

	const requestQuery = useCallback(
		async (
			source: string,
			pipeline: Record<string, unknown>[],
		): Promise<Record<string, unknown>[]> => {
			await awaitReady();

			return new Promise((resolve, reject) => {
				const id = window.crypto.randomUUID();

				queryPending.current.set(id, { resolve, reject });

				const request: WorkerRequest = { type: 'query', id, source, pipeline };

				workerRef.current?.postMessage(request);
			});
		},
		[awaitReady],
	);

	const loading = useMemo(() => ANALYTICS_LOADING_STATUSES.includes(status), [status]);

	const isSyncStale = useCallback((stored: string | null) => {
		let cutoff = moment.utc().startOf('day').hour(3);

		if (moment.utc().isBefore(cutoff)) {
			cutoff = cutoff.subtract(1, 'day');
		}

		return !stored || moment(stored).isBefore(cutoff);
	}, []);

	const value = useMemo<AnalyticsContextValue>(
		() => ({
			error,
			status,
			loading,
			filters,
			lastSyncedAt,
			groupSelection,
			showEngagementAnalytics,
			loadMetrics,
			requestRecord,
			requestCycles,
			requestCycleTemplates,
			requestScore,
			requestQuery,
			selectableGroupsFilter,
		}),
		[
			error,
			status,
			loading,
			filters,
			lastSyncedAt,
			groupSelection,
			showEngagementAnalytics,
			loadMetrics,
			requestRecord,
			requestCycles,
			requestCycleTemplates,
			requestScore,
			requestQuery,
			selectableGroupsFilter,
		],
	);

	// Re-read the marker whenever the database changes identity: the initial
	// render happens before the user assertion is seeded.
	useEffect(() => {
		if (!sync) return;

		setLastSyncedAt(localStorage.getItem(sync) ?? '');
	}, [sync]);

	useEffect(() => {
		if (!sync) return;

		if (dbEmpty || isSyncStale(localStorage.getItem(sync))) {
			loadMetrics();
		}
	}, [sync, loadMetrics, lastSyncedAt, dbEmpty, isSyncStale]);

	useEffect(() => {
		setError(null);
		setStatus(AnalyticsStatus.Opening);
		// The flag describes the database the previous worker opened; the init
		// handshake below re-reports it for this one.
		setDbEmpty(false);

		if (!database || !selectableIds) return;

		const selectable = selectableIds;

		const storedSelectable = localStorage.getItem(selectableKey);

		if (!storedSelectable) {
			localStorage.setItem(selectableKey, selectable);
		}

		if (storedSelectable && storedSelectable !== selectable) {
			clearDatabase(database)
				.then(() => {
					setLastSyncedAt('');
					setWorkerReset((count) => count + 1);
				})
				.catch((cause) =>
					addToast(`${t('errors.common.internalServerError')}: ${cause}`, {
						appearance: 'error',
						autoDismiss: true,
					}),
				);

			return;
		}

		const worker = new AnalyticsWorker();

		workerRef.current = worker;

		worker.postMessage({ type: 'init', name: database });

		worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
			const msg = e.data;

			if (msg.type === 'status') {
				setStatus(msg.status);

				// NotExists means the load ended without a snapshot: no
				// 'synced' will follow, so unblock waiting queries.
				if (msg.status === AnalyticsStatus.NotExists) lowerSyncBarrier();
			}

			if (msg.type === 'ready') {
				if (msg.empty) setDbEmpty(true);

				if (!msg.empty && !isSyncStale(localStorage.getItem(sync))) {
					setStatus(AnalyticsStatus.Succeeded);
				}

				readyRef.current.resolve();
			}

			if (msg.type === 'error') {
				setError(new Error(msg.message));
				setStatus(AnalyticsStatus.Failed);
				lowerSyncBarrier();

				const generic = t('errors.common.internalServerError');
				const detailed =
					msg.message && msg.message !== 'errors.common.internalServerError'
						? `${generic}: ${msg.message}`
						: generic;

				addToast(detailed, { appearance: 'error', autoDismiss: true });
			}

			if (msg.type === 'synced') {
				localStorage.setItem(sync, msg.syncedAt);

				setDbEmpty(false);
				setLastSyncedAt(msg.syncedAt);
				setStatus(AnalyticsStatus.Succeeded);
				lowerSyncBarrier();

				const searchParams = new URLSearchParams(history.location.search);

				searchParams.set('synced', `${Date.now().valueOf() % 600000}`);

				history.replace({
					pathname: history.location.pathname,
					search: searchParams.toString(),
				});
			}

			if (msg.type === 'record') {
				const pending = recordPending.current.get(msg.id);
				if (!pending) return;
				recordPending.current.delete(msg.id);
				pending.resolve(msg.data ?? null);
			}

			if (msg.type === 'cycles') {
				const pending = cyclesPending.current.get(msg.id);
				if (!pending) return;
				cyclesPending.current.delete(msg.id);
				pending.resolve(msg.data);
			}

			if (msg.type === 'cycleTemplates') {
				const pending = cycleTemplatesPending.current.get(msg.id);
				if (!pending) return;
				cycleTemplatesPending.current.delete(msg.id);
				pending.resolve(msg.data);
			}

			if (msg.type === 'score') {
				const pending = scorePending.current.get(msg.id);
				if (!pending) return;
				scorePending.current.delete(msg.id);
				pending.resolve({ analytics: msg.data, series: msg.series ?? [] });
			}

			if (msg.type === 'query') {
				const pending = queryPending.current.get(msg.id);
				if (!pending) return;
				queryPending.current.delete(msg.id);

				if (msg.error !== undefined) pending.reject(new Error(msg.error));
				else pending.resolve(msg.rows ?? []);
			}
		};

		return () => {
			worker.postMessage({ type: 'abort' });
			worker.terminate();

			if (workerRef.current === worker) {
				workerRef.current = null;
			}

			readyRef.current = createHandshake();

			// An aborted load never posts a status, so the barrier would stay
			// raised forever without this.
			lowerSyncBarrier();

			const restarted = new Error('Analytics worker restarted');

			for (const map of [
				recordPending,
				cyclesPending,
				cycleTemplatesPending,
				scorePending,
				queryPending,
			] as const) {
				map.current.forEach(({ reject }: IPendingRequest<never>) => reject(restarted));
				map.current.clear();
			}
		};
	}, [
		history,
		selectableIds,
		t,
		addToast,
		database,
		sync,
		selectableKey,
		lowerSyncBarrier,
		isSyncStale,
		workerReset,
	]);

	useEffect(() => {
		const searchParams = new URLSearchParams(history.location.search);

		searchParams.set('synced', `${Date.now().valueOf() % 600000}`);

		history.replace({
			pathname: history.location.pathname,
			search: searchParams.toString(),
		});
	}, [history]);

	return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics(): AnalyticsContextValue {
	const context = useContext(AnalyticsContext);

	if (!context) throw new Error('useAnalytics should be in AnalyticsProvider');

	return context;
}
